import { StandardEvents } from '@shopify/events';
import { preloadMedia } from './atelea-media-cache.js?v=2';

const PDP_MEDIA_SELECTOR = '[data-testid="product-information-media"]';
const PDP_INFO_SELECTOR = '[data-testid="product-information"]';
const PDP_MEDIA_MANIFEST_SELECTOR = '[data-atelea-product-media-manifest]';
const CARD_SELECTOR = 'product-card';
const TRANSITION_CLASS = 'atelea-media-transition';
const LOADING_DELAY = 180;
const FADE_DURATION = 160;
const IMAGE_TIMEOUT = 5000;
const transitionStates = new WeakMap();
let mediaManifestElement = null;
let mediaManifest = null;

/**
 * @typedef {object} AteleaTransitionState
 * @property {HTMLElement} owner
 * @property {HTMLElement} overlay
 * @property {HTMLImageElement} previousImage
 * @property {number} delayTimer
 * @property {number} removeTimer
 * @property {string} [variantId]
 * @property {HTMLImageElement} [targetImage]
 * @property {ResizeObserver} [resizeObserver]
 */

/**
 * @typedef {object} AteleaVariantMedia
 * @property {string} src
 * @property {string} [srcset]
 * @property {string} [sizes]
 */

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function nextPaint() {
  return new Promise((resolve) => window.requestAnimationFrame(resolve));
}

/**
 * @param {HTMLElement} owner
 * @returns {HTMLElement | null}
 */
function findMediaFrame(owner) {
  return (
    owner.querySelector('slideshow-component > slideshow-container') ??
    owner.querySelector('slideshow-container') ??
    owner.querySelector('.product-media')
  );
}

/**
 * @param {HTMLElement} frame
 * @returns {HTMLImageElement | null}
 */
function findVisibleImage(frame) {
  return (
    frame.querySelector("slideshow-slide[aria-hidden='false'] img.product-media__image") ??
    frame.querySelector('img.product-media__image')
  );
}

/**
 * @param {Element} target
 * @returns {{ owner: HTMLElement, frame: HTMLElement } | null}
 */
function getTransitionContext(target) {
  const card = target.closest(CARD_SELECTOR);
  if (card instanceof HTMLElement) {
    const owner = card.querySelector('.card-gallery');
    const frame = owner instanceof HTMLElement ? findMediaFrame(owner) : null;
    if (owner instanceof HTMLElement && frame) return { owner, frame };
    return null;
  }

  if (!target.closest('variant-picker')) return null;

  const owner = document.querySelector(PDP_MEDIA_SELECTOR);
  const frame = owner instanceof HTMLElement ? findMediaFrame(owner) : null;
  if (owner instanceof HTMLElement && frame) return { owner, frame };
  return null;
}

/**
 * @param {HTMLElement} owner
 */
function clearTransition(owner) {
  const state = transitionStates.get(owner);
  if (!state) return;

  window.clearTimeout(state.delayTimer);
  window.clearTimeout(state.removeTimer);
  state.resizeObserver?.disconnect();
  state.overlay.remove();
  owner.removeAttribute('data-atelea-media-loading');
  owner.removeAttribute('aria-busy');
  transitionStates.delete(owner);
}

/**
 * @param {AteleaTransitionState} state
 * @param {HTMLElement} frame
 */
function positionTransition(state, frame) {
  const ownerRect = state.owner.getBoundingClientRect();
  const frameRect = frame.getBoundingClientRect();
  state.overlay.style.top = `${frameRect.top - ownerRect.top}px`;
  state.overlay.style.left = `${frameRect.left - ownerRect.left}px`;
  state.overlay.style.width = `${frameRect.width}px`;
  state.overlay.style.height = `${frameRect.height}px`;
}

/**
 * @param {HTMLElement} owner
 * @param {HTMLElement} frame
 * @returns {AteleaTransitionState | null}
 */
function beginTransition(owner, frame) {
  if (prefersReducedMotion()) return null;

  const currentState = transitionStates.get(owner);
  const previewImage = currentState?.overlay.dataset.preview === 'true'
    ? currentState.targetImage
    : null;
  const sourceImage = previewImage || currentState?.previousImage || findVisibleImage(frame);
  if (!(sourceImage instanceof HTMLImageElement)) return null;

  clearTransition(owner);

  const overlay = document.createElement('div');
  overlay.className = TRANSITION_CLASS;
  overlay.dataset.state = 'loading';
  overlay.setAttribute('aria-hidden', 'true');

  const image = document.createElement('img');
  image.className = `${TRANSITION_CLASS}__image ${TRANSITION_CLASS}__image--previous`;
  image.src = sourceImage.currentSrc || sourceImage.src;
  image.alt = '';
  image.decoding = 'async';
  overlay.append(image);

  owner.append(overlay);

  /** @type {AteleaTransitionState} */
  const state = {
    owner,
    overlay,
    previousImage: image,
    delayTimer: window.setTimeout(() => {
      overlay.dataset.delayed = 'true';
    }, LOADING_DELAY),
    removeTimer: 0,
  };

  transitionStates.set(owner, state);
  owner.dataset.ateleaMediaLoading = 'true';
  owner.setAttribute('aria-busy', 'true');
  positionTransition(state, frame);

  if (typeof ResizeObserver !== 'undefined') {
    state.resizeObserver = new ResizeObserver(() => {
      const currentFrame = findMediaFrame(owner);
      if (currentFrame && transitionStates.get(owner) === state) {
        positionTransition(state, currentFrame);
      }
    });
    state.resizeObserver.observe(owner);
  }

  return state;
}

/**
 * @param {Element} target
 * @returns {string}
 */
function getVariantId(target) {
  if (target instanceof HTMLSelectElement) {
    return target.selectedOptions[0]?.dataset.variantId || '';
  }

  return target instanceof HTMLInputElement
    ? target.dataset.variantId || target.dataset.firstAvailableOrFirstVariantId || ''
    : '';
}

/**
 * @param {Element} target
 * @returns {HTMLInputElement | HTMLSelectElement | null}
 */
function getVariantControl(target) {
  if (target instanceof HTMLInputElement || target instanceof HTMLSelectElement) return target;

  const control = target.closest('variant-picker input, variant-picker select');
  if (control instanceof HTMLInputElement || control instanceof HTMLSelectElement) return control;

  const label = target.closest(
    'variant-picker label, swatches-variant-picker-component label'
  );
  const input = label?.querySelector('input');
  return input instanceof HTMLInputElement ? input : null;
}

/**
 * @param {Element} target
 * @returns {boolean}
 */
function canPreviewTarget(target) {
  const input = getVariantControl(target);
  if (input?.dataset.optionAvailable === 'false') return false;

  const picker = target.closest('variant-picker');
  if (!picker) return true;

  return picker.querySelectorAll('fieldset, .variant-option--dropdowns').length <= 1;
}

/**
 * @param {Element} target
 * @returns {Map<string, AteleaVariantMedia> | null}
 */
function getProductMediaManifest(target) {
  const info = target.closest(PDP_INFO_SELECTOR);
  const source = info?.querySelector(PDP_MEDIA_MANIFEST_SELECTOR) ??
    document.querySelector(PDP_MEDIA_MANIFEST_SELECTOR);
  if (!(source instanceof HTMLScriptElement)) return null;
  if (source === mediaManifestElement) return mediaManifest;

  try {
    const parsed = JSON.parse(source.textContent || '{}');
    const entries = Object.entries(parsed.variants || {}).filter(([, media]) => media?.src);
    mediaManifest = new Map(entries);
    mediaManifestElement = source;
    return mediaManifest;
  } catch {
    mediaManifest = null;
    mediaManifestElement = source;
    return null;
  }
}

/**
 * @param {Element} target
 * @returns {AteleaVariantMedia | null}
 */
function getTargetMedia(target) {
  if (!canPreviewTarget(target)) return null;

  const card = target.closest(CARD_SELECTOR);
  if (card instanceof HTMLElement) {
    const input = getVariantControl(target);
    const payload = input?.dataset.ateleaMedia;
    if (!payload) return null;

    try {
      const media = JSON.parse(payload);
      return Array.isArray(media) && media[0]?.src ? media[0] : null;
    } catch {
      return null;
    }
  }

  const variantId = getVariantId(target);
  return variantId ? getProductMediaManifest(target)?.get(variantId) || null : null;
}

/**
 * @param {Element} target
 * @returns {HTMLImageElement | null}
 */
function getTargetImage(target) {
  const card = target.closest(CARD_SELECTOR);
  if (card instanceof HTMLElement) {
    const image = card.querySelector('.card-gallery img.product-media__image');
    return image instanceof HTMLImageElement ? image : null;
  }

  const image = document.querySelector(`${PDP_MEDIA_SELECTOR} img.product-media__image`);
  return image instanceof HTMLImageElement ? image : null;
}

/**
 * @param {Element} target
 * @param {'auto' | 'high'} [priority]
 */
function warmVariantMedia(target, priority = 'auto') {
  const media = getTargetMedia(target);
  if (!media) return;

  const currentImage = getTargetImage(target);
  const sizes = currentImage instanceof HTMLImageElement
    ? currentImage.getAttribute('sizes')?.replace(/^auto,\s*/, '') || media.sizes
    : media.sizes;
  const requestMedia = { ...media, sizes };
  return { media: requestMedia, promise: preloadMedia(requestMedia, priority) };
}

/**
 * @param {AteleaTransitionState | null} state
 * @param {AteleaVariantMedia} media
 */
function showTargetPreview(state, media) {
  if (!state || transitionStates.get(state.owner) !== state) return;
  if (state.overlay.dataset.state === 'leaving' || state.overlay.dataset.state === 'failed') return;

  const image = document.createElement('img');
  image.className = `${TRANSITION_CLASS}__image ${TRANSITION_CLASS}__image--target`;
  image.alt = '';
  image.decoding = 'async';
  if (media.srcset) image.srcset = media.srcset;
  if (media.sizes) image.sizes = media.sizes;
  image.src = media.src;
  state.overlay.append(image);
  state.targetImage = image;

  window.requestAnimationFrame(() => {
    if (!state || transitionStates.get(state.owner) !== state) return;
    const stateName = state.overlay.dataset.state;
    if (stateName === 'leaving' || stateName === 'failed') return;

    state.overlay.dataset.preview = 'true';
    state.overlay.dataset.state = 'preview';
    delete state.overlay.dataset.delayed;
  });
}

/**
 * @param {AteleaTransitionState | null} state
 */
function revertTargetPreview(state) {
  if (!state) return;

  state.targetImage?.remove();
  state.targetImage = undefined;
  delete state.overlay.dataset.preview;
}

/**
 * @param {Event} event
 */
function handleVariantIntent(event) {
  if (!(event.target instanceof Element)) return;

  const control = getVariantControl(event.target);
  if (!control) return;
  const related = /** @type {Node | null} */ (event.relatedTarget);
  const owner = control.closest('label') || control;
  if (related instanceof Node && owner.contains(related)) return;

  warmVariantMedia(control, event.type === 'pointerdown' ? 'high' : 'auto');
}

/**
 * @param {HTMLImageElement | null} image
 * @returns {Promise<boolean>}
 */
function waitForImage(image) {
  if (!(image instanceof HTMLImageElement)) return Promise.resolve(false);

  return new Promise((resolve) => {
    let settled = false;
    let timeoutId = 0;

    const settle = (loaded) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      image.removeEventListener('load', handleLoad);
      image.removeEventListener('error', handleError);
      resolve(loaded);
    };

    const handleLoad = () => {
      Promise.resolve()
        .then(() => image.decode?.())
        .catch(() => {})
        .finally(() => settle(image.naturalWidth > 0));
    };
    const handleError = () => settle(false);

    if (image.complete) {
      if (image.naturalWidth > 0) {
        handleLoad();
      } else {
        settle(false);
      }
      return;
    }

    image.addEventListener('load', handleLoad);
    image.addEventListener('error', handleError);
    timeoutId = window.setTimeout(() => settle(image.naturalWidth > 0), IMAGE_TIMEOUT);
  });
}

/**
 * @param {AteleaTransitionState | null} state
 */
function finishTransition(state) {
  if (!state || transitionStates.get(state.owner) !== state) return;

  window.clearTimeout(state.delayTimer);
  state.overlay.dataset.state = 'leaving';
  delete state.overlay.dataset.delayed;
  state.removeTimer = window.setTimeout(() => clearTransition(state.owner), FADE_DURATION + 40);
}

/**
 * @param {AteleaTransitionState | null} state
 */
async function settleTransition(state) {
  if (!state || transitionStates.get(state.owner) !== state) return;

  await nextPaint();
  if (transitionStates.get(state.owner) !== state) return;

  const frame = findMediaFrame(state.owner);
  if (!frame) {
    holdTransition(state);
    return;
  }

  positionTransition(state, frame);
  const loaded = await waitForImage(findVisibleImage(frame));
  if (!loaded) {
    holdTransition(state);
    return;
  }
  finishTransition(state);
}

/**
 * Keeps the decoded previous image over a failed or stalled replacement.
 *
 * @param {AteleaTransitionState | null} state
 */
function holdTransition(state) {
  if (!state || transitionStates.get(state.owner) !== state) return;

  revertTargetPreview(state);
  window.clearTimeout(state.delayTimer);
  window.clearTimeout(state.removeTimer);
  state.overlay.dataset.state = 'failed';
  state.overlay.dataset.failed = 'true';
  state.owner.dataset.ateleaMediaLoading = 'error';
  state.owner.setAttribute('aria-busy', 'false');
}

/**
 * @param {Event} event
 */
function handleVariantChange(event) {
  if (!event.isTrusted || !(event.target instanceof Element)) return;

  const context = getTransitionContext(event.target);
  if (!context) return;

  const warm = warmVariantMedia(event.target, 'high');
  const state = beginTransition(context.owner, context.frame);
  if (!state) return;
  state.variantId = getVariantId(event.target);

  warm?.promise.then((loaded) => {
    if (loaded) showTargetPreview(state, warm.media);
  });
}

/**
 * @param {import('@shopify/events').ProductSelectEvent} event
 */
function handleProductSelect(event) {
  if (!(event.target instanceof Element) || !event.promise) return;

  const context = getTransitionContext(event.target);
  if (!context) return;

  const state = transitionStates.get(context.owner);
  const eventVariantId = event.detail?.variantId ? String(event.detail.variantId) : '';
  if (!state || (state.variantId && eventVariantId && state.variantId !== eventVariantId)) return;

  event.promise.then(() => settleTransition(state)).catch((error) => {
    if (transitionStates.get(context.owner) !== state) return;
    if (error?.name === 'AbortError') {
      clearTransition(state.owner);
      return;
    }
    console.warn('[atelea-media-transition] Variant media update failed:', error);
    holdTransition(state);
  });
}

document.addEventListener('change', handleVariantChange, true);
document.addEventListener(StandardEvents.productSelect, handleProductSelect, true);
document.addEventListener('pointerover', handleVariantIntent, true);
document.addEventListener('pointerdown', handleVariantIntent, true);
document.addEventListener('focusin', handleVariantIntent, true);
